import { Box, Flex, Input, Loader, ScrollArea, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import Close from '@/assets/icons/cross-circle-fill.svg'
import Exclamation from '@/assets/icons/exclamation.svg'
import Search from '@/assets/icons/search.svg'
import ResponsiveModal from '@/components/basic/ResponsiveModal'

import CoinRow from './CoinRow'
import { CoinListType, useDifferenceTypeCoinList, useSearchCoins } from './hooks'
import styles from './index.module.css'
import TypeItem from './TypeItem'

interface CoinListModalProps {
  visible: boolean
  onClose: () => void
}

const CONTENT_HEIGHT = 384

function useCoinListTypes() {
  const t = useTranslations('common.coinCategoryTypes')
  return useMemo(
    () => [
      {
        type: CoinListType.Trending,
        label: t('trending'),
      },
      {
        type: CoinListType.TopGainers,
        label: t('topGainers'),
      },
      {
        type: CoinListType.TopLosers,
        label: t('topLosers'),
      },
      {
        type: CoinListType.RecentlyAdded,
        label: t('recentlyAdded'),
      },
      {
        type: CoinListType.TokensToWatch,
        label: t('tokensToWatch'),
      },
      {
        type: CoinListType.MostPopular,
        label: t('mostPopular'),
      },
    ],
    [t],
  )
}

export default function CoinListModal({ visible, onClose }: CoinListModalProps) {
  const [activeCoinListType, setActiveCoinListType] = useState(CoinListType.Trending)
  const { displayCoinRes } = useDifferenceTypeCoinList(activeCoinListType)
  const { setKeyword, keyword, coinList, isSearching } = useSearchCoins()
  const t = useTranslations('coinListModal')
  const coinListTypes = useCoinListTypes()
  const content = (() => {
    const displayLoading = keyword ? isSearching : displayCoinRes.isLoading
    if (displayLoading)
      return (
        <Flex h={CONTENT_HEIGHT} align={'center'} justify={'center'}>
          <Loader />
        </Flex>
      )
    const coins = keyword ? coinList : displayCoinRes.coins
    if (coins.length === 0) {
      return (
        <Flex h={CONTENT_HEIGHT} direction={'column'} align={'center'} justify={'center'}>
          <Exclamation width={80} height={80} />
          <Text mt={12} variant={'body-emphasis'}>
            {t('noResultsFound')}
          </Text>
        </Flex>
      )
    }
    return (
      <ScrollArea h={CONTENT_HEIGHT} scrollbars={'y'} type={'scroll'}>
        {coins.map(coin => (
          <CoinRow key={coin.slug} coin={coin} />
        ))}
      </ScrollArea>
    )
  })()
  return (
    <ResponsiveModal
      title={<Text variant={'heading2'}>{t('selectACoin')}</Text>}
      opened={visible}
      onClose={onClose}
    >
      <Box>
        <Input
          classNames={{
            input: styles.searchInput,
          }}
          placeholder={t('searchCoin')}
          leftSection={
            <Box mx={12}>
              <Search width={20} height={20} fill={'var(--content-secondary)'} />
            </Box>
          }
          rightSectionPointerEvents="all"
          rightSection={
            keyword ? <Close className={styles.clearIcon} onClick={() => setKeyword('')} /> : null
          }
          value={keyword}
          onChange={setKeyword}
        />
        {!keyword ? (
          <ScrollArea w={'100%'} scrollbars={'x'} mt={16} type={'scroll'}>
            <Flex direction={'row'}>
              {coinListTypes.map(item => (
                <TypeItem
                  key={item.type}
                  label={item.label}
                  isActive={item.type === activeCoinListType}
                  onClick={() => setActiveCoinListType(item.type)}
                />
              ))}
            </Flex>
          </ScrollArea>
        ) : null}

        <Stack mt={12} key={activeCoinListType}>
          {content}
        </Stack>
      </Box>
    </ResponsiveModal>
  )
}
